import { trpc, trpcClient } from '@/trpcClient';
import type { FlowSensorEventDto } from '@shelflix/trpc/routes/flowSensor/events/useCase';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { toPeriodInput, type FlowSensorFilterValue } from './FlowSensorFilter';
import { formatDateTime } from './format';

const PAGE_SIZE = 20;
const EXPORT_LIMIT = 10000;

const csvCell = (v: string) => `"${v.replace(/"/g, '""')}"`;

type EventRow = FlowSensorEventDto & { uuid: string }; // GenericTable usa uuid como chave da linha

export function EventsTab({ filter }: { filter: FlowSensorFilterValue }) {
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  useEffect(() => setPage(1), [filter]);

  const { data, isLoading, isError } = useQuery({
    ...trpc.flowSensor.events.queryOptions({ ...toPeriodInput(filter), page, recordsPerPage: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  // CSV para Excel (separador ";" e BOM UTF-8), com data e hora de Brasília.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const result = await trpcClient.flowSensor.events.query({
        ...toPeriodInput(filter),
        page: 1,
        recordsPerPage: EXPORT_LIMIT,
      });
      const lines = [
        ['Data e hora', 'Permanência (s)', 'Sensor', 'MAC', 'Produto', 'Estabelecimento', 'Hora aproximada'].join(';'),
        ...result.items.map((e) =>
          [
            formatDateTime(e.occurredAt),
            e.durationS.toFixed(1).replace('.', ','),
            csvCell(e.sensorName),
            e.mac,
            csvCell(e.product ?? ''),
            csvCell(e.facilityName ?? ''),
            e.approximate ? 'sim' : 'não',
          ].join(';')
        ),
      ];
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `aproximacoes_${filter.from}_${filter.to}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      if (result.total > EXPORT_LIMIT) toast.warning(`Exportadas as ${EXPORT_LIMIT} mais recentes de ${result.total}.`);
    } catch (error) {
      toast.error(`Erro ao exportar: ${(error as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const columns: Array<{
    header: string;
    key: keyof EventRow;
    render?: (value: EventRow[keyof EventRow], row: EventRow) => React.ReactNode;
  }> = [
    {
      header: 'Data e hora',
      key: 'occurredAt',
      render: (_, row) => (
        <span title={row.approximate ? 'Hora calculada pelo sensor (relógio ainda sem internet)' : undefined}>
          {formatDateTime(row.occurredAt)}
          {row.approximate ? ' *' : ''}
        </span>
      ),
    },
    { header: 'Permanência', key: 'durationS', render: (_, row) => `${row.durationS.toFixed(1).replace('.', ',')} s` },
    { header: 'Sensor', key: 'sensorName' },
    { header: 'Produto', key: 'product', render: (_, row) => row.product ?? '—' },
    { header: 'Estabelecimento', key: 'facilityName', render: (_, row) => row.facilityName ?? '—' },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--gray-secondary)]">
          {data ? `${data.total.toLocaleString('pt-BR')} aproximação(ões) no período` : ''}
        </p>
        <Button variant="outline" onClick={exportCsv} disabled={exporting || !data?.total}>
          <Icon name="download" className="mr-1" /> {exporting ? 'Exportando...' : 'Exportar CSV'}
        </Button>
      </div>
      {isLoading && <p className="p-4">Carregando eventos...</p>}
      {isError && <p className="p-4 text-red-500">Erro ao carregar eventos.</p>}
      {data && (
        <GenericTable
          columns={columns}
          data={data.items.map((e) => ({ ...e, uuid: String(e.id) }))}
          onPageChange={setPage}
          itemsPerPages={PAGE_SIZE}
          totalItems={data.total}
          page={page}
        />
      )}
    </div>
  );
}
